import {
  createEmptyAttribution,
  hasAttribution,
  IAttribution,
  parseAttribution,
} from '@pharmaplan/common';

import { isAnalyticsConsentRefused } from './cookieConsent';

const ATTRIBUTION_STORAGE_KEY = 'ppAttribution';
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface IStoredAttribution {
  attribution: IAttribution;
  capturedAt: number;
}

// pharmaplan.ca AND app.pharmaplan.ca ARE ONE SITE FOR ATTRIBUTION PURPOSES
const getRootDomain = (hostname: string) =>
  hostname.split('.').slice(-2).join('.');

const getExternalReferrerHost = (): string | null => {
  if (!document.referrer) return null;

  try {
    const { hostname } = new URL(document.referrer);
    const isInternalReferrer = getRootDomain(hostname) === getRootDomain(window.location.hostname);
    if (isInternalReferrer) return null;

    return hostname;
  } catch {
    return null;
  }
};

// ONLY THE FIRST PATH SEGMENT IS KEPT SO NO IDENTIFIER IN A URL IS STORED
const getLandingSection = (pathname: string) =>
  `/${pathname.split('/')[1] ?? ''}`;

const readStoredValue = (): IStoredAttribution | null => {
  try {
    const value = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (value === null) return null;

    return JSON.parse(value) as IStoredAttribution;
  } catch {
    return null;
  }
};

const saveAttribution = (attribution: IAttribution) => {
  const storedValue: IStoredAttribution = { attribution, capturedAt: Date.now() };

  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(storedValue));
  } catch {
    // STORAGE IS UNAVAILABLE (E.G. PRIVATE BROWSING): ATTRIBUTION IS NOT KEPT
  }
};

// CAMPAIGN DATA IS ANALYTICS, SO A VISITOR WHO REFUSED COOKIES NEVER PASSES IT ON
export const readStoredAttribution = (): IAttribution | null => {
  if (isAnalyticsConsentRefused()) return null;

  const storedValue = readStoredValue();
  if (storedValue === null) return null;

  const isExpired = Date.now() - storedValue.capturedAt > ATTRIBUTION_MAX_AGE_MS;
  if (isExpired) return null;

  return storedValue.attribution;
};

// RUNS ONCE BEFORE THE APP RENDERS, BEFORE ANY ROUTER REDIRECT DROPS THE QUERY.
// A DIRECT VISIT NEVER OVERWRITES A STORED CAMPAIGN (LAST NON-DIRECT TOUCH)
export const captureLandingAttribution = () => {
  if (isAnalyticsConsentRefused()) return;

  const { search, pathname } = window.location;
  const attributionFromQuery = parseAttribution(search);
  const attribution: IAttribution = {
    ...(attributionFromQuery ?? createEmptyAttribution()),
    pp_ref: attributionFromQuery?.pp_ref ?? getExternalReferrerHost(),
    pp_path: getLandingSection(pathname),
  };

  if (!hasAttribution(attribution)) return;

  saveAttribution(attribution);
};
